import QuickAddDrawer from "components/quickAddDrawer";
import StockOut from "components/stockOut";
import { useRef, useState, memo, useMemo, useCallback } from "react";
import {
  FaArrowRight,
  FaCartPlus,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { HiShoppingCart } from "react-icons/hi";
import { TbCurrencyTaka } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import ProductSliderShimmer from "./shimmer/productSliderShimmer";
import useHorizontalScrollButtons from "../utils/useHorizontalScrollButtons";
import { clearCartItems, getCartItems, setCartItems } from "utils/cartStorage";

const ProductSlider = ({
  title,
  viewAllLink,
  productsItem = [],
  loading = false,
}) => {
  const scrollRef = useRef(null);
  const leftBtnRef = useRef(null);
  const rightBtnRef = useRef(null);
  const { scroll } = useHorizontalScrollButtons({
    scrollRef,
    leftBtnRef,
    rightBtnRef,
    dependency: [productsItem],
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigate = useNavigate();

  // 🔥 Memoized filtered products (prevents recalculation)
  const products = useMemo(() => {
    return (productsItem || []).filter(
      (item) => item.hideStatus === "available",
    );
  }, [productsItem]);

  const addToCart = useCallback((item) => {
    const existingItems = getCartItems();

    const firstVariant = item.variants?.[0] || {};
    const colorArray = item.color
      ? item.color.split(",").map((s) => s.trim())
      : [];

    const sizeArray = item.size
      ? item.size.split(",").map((s) => s.trim())
      : [];

    const selectedColor = colorArray[0] || "";
    const selectedSize = sizeArray[0] || "";
    const selectedTitle = firstVariant.title || "";
    const selectedPrice = firstVariant.flashPrice || 0;

    const existingIndex = existingItems.findIndex(
      (p) => p.item_id === item.item_id,
    );

    if (existingIndex !== -1) {
      existingItems[existingIndex] = {
        ...existingItems[existingIndex],
        quantity: (existingItems[existingIndex].quantity += 1),
        selectedColor,
        selectedSize,
        selectedTitle,
        selectedPrice,
      };
    } else {
      existingItems.push({
        ...item,
        quantity: 1,
        selectedColor,
        selectedSize,
        selectedTitle,
        selectedPrice,
        selectedVariant: firstVariant,
      });
    }
    setCartItems(existingItems);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        currency: "BDT",
        value: selectedPrice,
        items: [
          {
            item_id: item.item_id,
            item_name: firstVariant.title,
            price: selectedPrice,
            item_category: item.catalogue_title,
            quantity: 1,
          },
        ],
      },
    });
  }, []);

  if (loading) {
    return <ProductSliderShimmer />;
  }

  if (!products?.length) return null;

  return (
    <div className="container mx-auto my-4">
      <div className="flex justify-between font-extrabold pb-2 px-4">
        <p className="md:text-2xl text-lg font-extrabold">{title}</p>

        <Link
          to={viewAllLink}
          className="flex items-center md:text-lg text-xs hover:text-[#265fc9]"
        >
          View All
          <FaArrowRight className="ml-2" />
        </Link>
      </div>

      <hr className="mx-4 relative mb-4" />

      {/* SLIDER */}
      <div className="relative mx-4">
        {/* LEFT BTN */}
        <button
          ref={leftBtnRef}
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-md p-2 rounded-full transition-opacity"
        >
          <FaChevronLeft />
        </button>

        <div
          ref={scrollRef}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-5"
        >
          {" "}
          <style>{`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
          `}</style>
          {products.map((item, index) => {
            const isOutOfStock = item.status === "unavailable";
            const firstVariant = item.variants?.[0] || {};
            const firstImage = firstVariant.images?.[0]?.thumb || "";

            const selectedTitle = firstVariant.title || "-";
            const selectedPrice = firstVariant.flashPrice || 0;
            const selectedDiscount = firstVariant.discount || 0;

            const originalPrice = selectedDiscount || 0;
            const discountPercent =
              originalPrice > 0
                ? Math.ceil(
                    ((originalPrice - selectedPrice) / originalPrice) * 100,
                  )
                : 0;

            return (
              <div
                key={`${item.item_id}-${index}`}
                className={`relative flex flex-col flex-shrink-0 lg:w-[240px] md:w-[240px] max-md:basis-[47%] bg-white rounded-[10px] overflow-hidden transition-transform duration-200
                        ${isOutOfStock ? "cursor-not-allowed opacity-40" : ""}
                    `}
              >
                {/* OUT OF STOCK OVERLAY (IMPORTANT - KEEP IT) */}
                {isOutOfStock && <StockOut />}

                <Link
                  to={
                    isOutOfStock
                      ? "#"
                      : `/details/${item.catalogue}/${item.item_id}`
                  }
                  onClick={(e) => {
                    if (isOutOfStock) e.preventDefault();
                  }}
                >
                  <div className="relative group aspect-product-desktop overflow-hidden">
                    <img
                      src={`https://backend.nuhababy.com/${firstImage}`}
                      alt={selectedTitle}
                      loading={index < 2 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      decoding="async"
                      width={315}
                      height={460}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />

                    {discountPercent > 0 && (
                      <div className="absolute top-0 left-0 w-20 h-20 overflow-hidden z-10">
                        <div className="absolute top-[18px] -left-[28px] rotate-[-45deg] bg-red-500 text-white text-[10px] md:text-xs font-bold w-32 text-center py-1 shadow-md">
                          {discountPercent}% OFF
                        </div>
                      </div>
                    )}
                    {firstVariant.freeVariantDelivery === 1 && (
                      <div className="absolute top-2 right-2">
                        <div className="flex items-center justify-center gap-1 px-2 md:h-8 h-6 bg-blue-600  text-white rounded-md text-[10px] md:text-xs font-semibold shadow">
                          🚚 <span>ফ্রি ডেলিভারী</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>

                <div className="md:px-3 p-2 flex flex-col flex-grow">
                  <div className="text-black hover:text-[#222836] hover:opacity-60 font-medium tracking-wide mb-1 text-sm whitespace-nowrap overflow-hidden truncate">
                    {item?.mainTitle}
                  </div>
                  <div className="text-black hover:text-[#222836] hover:opacity-60 tracking-wide mb-1 text-xs whitespace-nowrap overflow-hidden truncate">
                    {selectedTitle}
                  </div>

                  <div className="flex pb-2">
                    <div className="relative inline-flex items-center text-gray-400 text-sm mr-2">
                      <span className="flex items-center">
                        <p>{selectedDiscount}</p>
                        <TbCurrencyTaka className="font-semibold" />
                      </span>
                      <div className="absolute left-0 right-0 top-1/2 h-[1px] bg-gray-400"></div>
                    </div>
                    <div className="flex text-[#265fc9] items-center text-sm">
                      <p className="font-bold">{selectedPrice}</p>
                      <TbCurrencyTaka className="font-semibold" />
                    </div>
                  </div>

                  {!isOutOfStock && (
                    <div className="flex gap-2 my-1 mt-auto">
                      <button
                        lang="bn"
                        onClick={(e) => {
                          addToCart(item);
                          navigate("/orderDetails");
                        }}
                        className="md:text-base text-sm flex-1 text-white bg-backgroundColor border border-darkBgColor rounded-md md:px-4 px-2 py-1 md:font-bold font-semibold flex justify-center items-center"
                      >
                        অর্ডার করুন
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(item);
                          setDrawerOpen(true);
                        }}
                        className="md:px-3 px-2 rounded-md border text-darkBgColor py-1"
                      >
                        <span className="group-hover:hidden md:text-base text-sm">
                          <FaCartPlus />
                        </span>
                        <HiShoppingCart className="hidden group-hover:block w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          ref={rightBtnRef}
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-md p-2 rounded-full transition-opacity"
        >
          <FaChevronRight />
        </button>
      </div>

      <QuickAddDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

export default memo(ProductSlider);
